import {
  CampaignType,
  ChevronDownIcon,
  getEntityImage,
  getPurchaseRewardAmountForProductV2,
  hexadecimal,
  MOBILE_SCREEN_SIZE,
  RewardPillWithText,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";

import { useFastCheckoutContext } from "@/components/Checkout/CheckoutMachine";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import { useSelector } from "@xstate/react";
import { CheckoutInputV2 } from "../../CheckoutInputV2";
import {
  AccountConnectBenefits,
  AccountConnectRewards,
} from "../components/AccountRewards";
import { ACTION_REQUIRED_ERROR, FastCheckoutState } from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import { useEffect, useState } from "react";
import { useAppSelector } from "@/redux/hooks";
import { getRetailerUserNameFieldHeading } from "@/utils/StringUtils";
import ButtonContainer from "../components/Buttons/ButtonContainer";
import ClearButton from "../components/Buttons/ClearButton";
import SaveButton from "../components/Buttons/SaveButton";
import { UserInteractionType } from "@/components/Checkout/types";

const AccountPreviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: "16px",
  gap: "10px",
  color: theme.palette.primary.main,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
    gap: "7px",
  },
  ".logo": {
    width: "25px",
    height: "25px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: "20px",
      height: "20px",
    },
  },
}));

const AccountPreview = () => {
  const { actorRef } = useFastCheckoutContext();

  const retailerUsername = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo?.retailerUsername
  );
  const retailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );
  const retailerAccount = retailerUsername ?? "Unknown";

  return (
    <AccountPreviewContainer>
      {retailerId ? (
        <img
          alt="logo"
          className="logo"
          src={getEntityImage(retailerId, "retailer")}
        />
      ) : null}
      <div className="title">{retailerAccount}</div>
    </AccountPreviewContainer>
  );
};

const AccountInputFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: "24px",
  },
  ".input-container": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "column",
      gap: "15px",
    },
  },
}));

const UpdateUserNamePassword = () => {
  const { actorRef, send } = useFastCheckoutContext();
  const currentRetailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );
  const retailers = useAppSelector(({ retailers }) => retailers);
  const currentRetailer = retailers.find(
    (r) => r.retailerId === currentRetailerId
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.RETAILER_CREDENTIALS,
        interactionData: {
          credential: {
            username: email,
            password: password,
          },
          retailerId: currentRetailerId ?? 0,
        },
      },
    });
  };

  return (
    <AccountInputFormContainer>
      <div className={"input-container"}>
        <CheckoutInputV2
          label={
            currentRetailer
              ? getRetailerUserNameFieldHeading(currentRetailer)
              : "Username"
          }
          onChange={(e) => setEmail(e.target.value)}
          value={email}
        />
        <CheckoutInputV2
          label={"Password"}
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          type="password"
        />
      </div>
      <ButtonContainer>
        <ClearButton>Clear</ClearButton>
        <SaveButton onClick={handleSubmit}>Save</SaveButton>
      </ButtonContainer>
    </AccountInputFormContainer>
  );
};

const OTPEnter = () => {
  const mobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const [otp, setOtp] = useState("");
  const [resendCoolDown, setResendCoolDown] = useState(0);
  const { send } = useFastCheckoutContext();

  useEffect(() => {
    if (resendCoolDown > 0) {
      setTimeout(() => setResendCoolDown(resendCoolDown - 1), 1000);
    }
  }, [resendCoolDown]);

  const ResponsiveResendText = mobile ? "Resend code" : "Resend";

  const ResendText =
    resendCoolDown > 0 ? `Resend in ${resendCoolDown}s` : ResponsiveResendText;

  const handleSubmit = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.OTP_VERIFICATION,
        interactionData: {
          otpCode: otp,
        },
      },
    });
  };
  const handleResend = () => {
    setResendCoolDown(30);
  };

  return (
    <AccountInputFormContainer>
      <div className={"input-container"}>
        <CheckoutInputV2
          label={"Verification code"}
          onChange={(e) => setOtp(e.target.value)}
          value={otp}
        />
      </div>
      <ButtonContainer>
        <ClearButton disabled={resendCoolDown > 0} onClick={handleResend}>
          {ResendText}
        </ClearButton>
        <SaveButton onClick={handleSubmit}>Submit</SaveButton>
      </ButtonContainer>
    </AccountInputFormContainer>
  );
};

const RewardExpandContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  gap: 16,
  marginTop: 31,
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    marginTop: 24,
    gap: 8,
    flexDirection: "column",
  },
}));

const AccountSection = () => {
  const theme = useTheme();
  const { actorRef } = useFastCheckoutContext();
  const retailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );
  const userCheckoutInfo = useSelector(
    actorRef,
    (state) => state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.userCheckoutInfo
  );
  const products = useSelector(
    actorRef,
    (state) => state.context.retailerCheckoutOrderStatus?.products
  );

  const order = useSelector(
    actorRef,
    (state) => state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const status = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );
  const accountSectionState = useSelector(
    actorRef,
    (state) => state.context.sectionState.account
  );
  const { open, chevronShow, errorMessage } = accountSectionState;
  const error = useSelector(actorRef, (state) => state.context.error);
  const errorType = error?.type;

  const retailers = useAppSelector(({ retailers }) => retailers);

  const retailer = retailers.find((r) => r.retailerId === retailerId);

  const currentProducts =
    products &&
    order?.items.map((item) => {
      const product = products[item.stacklineSku];
      return {
        ...product,
        quantity: item.quantity,
      };
    });

  const purchaseReward = currentProducts?.reduce((acc, product) => {
    const { amount } = getPurchaseRewardAmountForProductV2(product);
    return acc + amount;
  }, 0) ?? 0;
  const connectReward = retailer?.retailerConnectRewardAmount ?? 0;
  const membershipReward = 0;
  const totalReward = connectReward + purchaseReward + membershipReward;
  const toggleOpen = () => {
    if (!chevronShow) {
      return;
    }
    if (open) {
      actorRef.send({
        type: "ON_SESSION_CLOSE",
        data: { sessionToClose: "account" },
      });
    } else {
      actorRef.send({
        type: "ON_SESSION_OPEN",
        data: { sessionToOpen: "account" },
      });
    }
  };

  // if there is no username, skip the account section for receipt generated state
  if (status === FastCheckoutState.RECEIPT_GENERATED && !userCheckoutInfo?.retailerUsername) {
    return null;
  }

  return (
    <Box className="section">
      {/* TODO: move to collapsible section */}
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">{"Account Method"}</div>
        {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>
      <StoreAnimatedHeightDiv>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        {errorType === ACTION_REQUIRED_ERROR.LOGIN_ERROR ? (
          <UpdateUserNamePassword />
        ) : errorType === ACTION_REQUIRED_ERROR.OTP_ERROR ? (
          <OTPEnter />
        ) : (
          <AccountPreview />
        )}
        <AnimatePresence mode="wait">
          <motion.div
            layout="position"
            key={open ? "view" : "edit"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            {open ? (
              <RewardExpandContainer>
                <AccountConnectRewards
                  retailerId={retailerId}
                  purchaseAmount={purchaseReward}
                  connectAmount={connectReward}
                  membershipAmount={membershipReward}
                />
                <AccountConnectBenefits retailerId={retailerId} />
              </RewardExpandContainer>
            ) : errorType !== ACTION_REQUIRED_ERROR.LOGIN_ERROR &&
              errorType !== ACTION_REQUIRED_ERROR.OTP_ERROR &&
              status !== FastCheckoutState.RECEIPT_GENERATED ? (
              <div style={{ marginTop: 8 }}>
                <RewardPillWithText
                  rewardAmount={totalReward}
                  rewardCampaignType={CampaignType.DailyDealsRewardCampaign}
                  rewardPercentTextOverride="total reward"
                  onPillClick={toggleOpen}
                  pillContainerStyle={{
                    backgroundColor: hexadecimal(theme.palette.secondary.main)(
                      40
                    ),
                    color: theme.palette.primary.main,
                  }}
                />
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </StoreAnimatedHeightDiv>
    </Box>
  );
};

export default AccountSection;
